import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { BackgroundTaskProvider } from './lib/BackgroundTaskContext';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { BackgroundTaskBar } from './components/BackgroundTaskBar';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { QuestionsPage } from './pages/QuestionsPage';
import { ChaptersPage } from './pages/ChaptersPage';
import { ImagesPage } from './pages/ImagesPage';
import { UsersPage } from './pages/UsersPage';
import { FeatureFlagsPage } from './pages/FeatureFlagsPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { AiDashboardPage } from './pages/AiDashboardPage';
import { AiPromptsPage } from './pages/AiPromptsPage';
import { LearningAnalyticsPage } from './pages/LearningAnalyticsPage';
import { SimulationAnalyticsPage } from './pages/SimulationAnalyticsPage';
import { GamificationPage } from './pages/GamificationPage';
import { BadgesPage } from './pages/BadgesPage';
import { SettingsPage } from './pages/SettingsPage';
import { SecurityPage } from './pages/SecurityPage';
import { LanguagesPage } from './pages/LanguagesPage';
import { ReportsPage } from './pages/ReportsPage';
import { SupportPage } from './pages/SupportPage';
import { SupportChatPage } from './pages/SupportChatPage';
import { RolesPage } from './pages/RolesPage';
import { ChurnPredictionPage } from './pages/ChurnPredictionPage';
import { ContentIntelligencePage } from './pages/ContentIntelligencePage';
import { PromptTestingPage } from './pages/PromptTestingPage';
import { ScheduledReportsPage } from './pages/ScheduledReportsPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { SystemStatusPage } from './pages/SystemStatusPage';
import { PricingPage } from './pages/PricingPage';
import { MarketingCreatePage } from './pages/MarketingCreatePage';
import { MarketingPostsPage } from './pages/MarketingPostsPage';
import { MarketingBrandingPage } from './pages/MarketingBrandingPage';
import { MarketingLearningPage } from './pages/MarketingLearningPage';
import { MarketingCampaignsPage } from './pages/MarketingCampaignsPage';
import { ImageReviewPage } from './pages/ImageReviewPage';
import { ContentQAPage } from './pages/ContentQAPage';
import { ImageCategoryPage } from './pages/ImageCategoryPage';
import { ImageSweepPage } from './pages/ImageSweepPage';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage';
import { SendLogPage } from './pages/SendLogPage';
import { LegalContentPage } from './pages/LegalContentPage';
import { ChangelogPage } from './pages/ChangelogPage';
import { TwoFAVerifyModal } from './components/TwoFAVerifyModal';
import type { Session } from '@supabase/supabase-js';
import { SalesDashboardPage } from './pages/sales/SalesDashboardPage';
import { SalesLeadsPage } from './pages/sales/SalesLeadsPage';
import { SalesPipelinePage } from './pages/sales/SalesPipelinePage';
import { SalesCampaignsPage } from './pages/sales/SalesCampaignsPage';
import { SalesPartnersPage } from './pages/sales/SalesPartnersPage';
import { SalesAffiliatePage } from './pages/sales/SalesAffiliatePage';
import { SalesCopilotPage } from './pages/sales/SalesCopilotPage';

export const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFAVerified, setTwoFAVerified] = useState(false);
  const [twoFAChecking, setTwoFAChecking] = useState(false);

  // Check if 2FA was already verified in this browser session (valid for 4h)
  const is2FASessionValid = (userId: string): boolean => {
    try {
      const cached = sessionStorage.getItem(`2fa_verified_${userId}`);
      if (!cached) return false;
      const { timestamp } = JSON.parse(cached);
      const FOUR_HOURS = 4 * 60 * 60 * 1000;
      return Date.now() - timestamp < FOUR_HOURS;
    } catch { return false; }
  };

  const mark2FAVerified = (userId: string) => {
    sessionStorage.setItem(`2fa_verified_${userId}`, JSON.stringify({ timestamp: Date.now() }));
  };

  const check2FAStatus = async (sess: Session) => {
    // Skip if already verified in this session
    if (is2FASessionValid(sess.user.id)) {
      setTwoFARequired(false);
      setTwoFAVerified(true);
      return;
    }

    setTwoFAChecking(true);
    try {
      const res = await fetch('/api/admin/2fa/status', {
        headers: { 'Authorization': `Bearer ${sess.access_token}` },
      });
      const data = await res.json();
      if (data.enabled) {
        setTwoFARequired(true);
        setTwoFAVerified(false);
      } else {
        setTwoFARequired(false);
        setTwoFAVerified(true);
      }
    } catch {
      // If check fails, skip 2FA (API might not have the route yet)
      setTwoFAVerified(true);
    } finally {
      setTwoFAChecking(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) check2FAStatus(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session && !twoFAVerified) {
        check2FAStatus(session);
      } else if (!session) {
        setTwoFARequired(false);
        setTwoFAVerified(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading || twoFAChecking) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="skeleton" style={{ width: 40, height: 40, borderRadius: '50%' }} />
      </div>
    );
  }

  if (!session) {
    return <LoginPage />;
  }

  if (twoFARequired && !twoFAVerified) {
    return (
      <TwoFAVerifyModal
        onVerified={() => {
          setTwoFAVerified(true);
          mark2FAVerified(session.user.id);
        }}
        onCancel={async () => {
          await supabase.auth.signOut();
          setTwoFARequired(false);
          setTwoFAVerified(false);
        }}
      />
    );
  }

  return (
    <BackgroundTaskProvider>
      <BrowserRouter>
        <div className="admin-layout">
          <Sidebar />
          <div className="admin-main">
            <Topbar />
            <main className="admin-content">
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/subscriptions" element={<SubscriptionsPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/chapters" element={<ChaptersPage />} />
                <Route path="/questions" element={<QuestionsPage />} />
                <Route path="/images" element={<ImagesPage />} />
                <Route path="/image-review" element={<ImageReviewPage />} />
                <Route path="/content-qa" element={<ContentQAPage />} />
                <Route path="/image-categories" element={<ImageCategoryPage />} />
                <Route path="/image-sweep" element={<ImageSweepPage />} />
                <Route path="/badges" element={<BadgesPage />} />
                <Route path="/ai-dashboard" element={<AiDashboardPage />} />
                <Route path="/ai-prompts" element={<AiPromptsPage />} />
                <Route path="/analytics/learning" element={<LearningAnalyticsPage />} />
                <Route path="/analytics/simulations" element={<SimulationAnalyticsPage />} />
                <Route path="/gamification" element={<GamificationPage />} />
                <Route path="/marketing/create" element={<MarketingCreatePage />} />
                <Route path="/marketing/posts" element={<MarketingPostsPage />} />
                <Route path="/marketing/branding" element={<MarketingBrandingPage />} />
                <Route path="/marketing/learnings" element={<MarketingLearningPage />} />
                <Route path="/marketing/campaigns" element={<MarketingCampaignsPage />} />
                <Route path="/email-templates" element={<EmailTemplatesPage />} />
                <Route path="/send-log" element={<SendLogPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/legal-content" element={<LegalContentPage />} />
                <Route path="/feature-flags" element={<FeatureFlagsPage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/audit-log" element={<AuditLogPage />} />
                <Route path="/languages" element={<LanguagesPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/support-chat" element={<SupportChatPage />} />
                <Route path="/roles" element={<RolesPage />} />
                <Route path="/churn" element={<ChurnPredictionPage />} />
                <Route path="/content-intelligence" element={<ContentIntelligencePage />} />
                <Route path="/prompt-testing" element={<PromptTestingPage />} />
                <Route path="/scheduled-reports" element={<ScheduledReportsPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/beta-feedback" element={<FeedbackPage />} />
                <Route path="/system-status" element={<SystemStatusPage />} />
                <Route path="/changelog" element={<ChangelogPage />} />
                <Route path="/sales" element={<SalesDashboardPage />} />
                <Route path="/sales/leads" element={<SalesLeadsPage />} />
                <Route path="/sales/pipeline" element={<SalesPipelinePage />} />
                <Route path="/sales/campaigns" element={<SalesCampaignsPage />} />
                <Route path="/sales/partners" element={<SalesPartnersPage />} />
                <Route path="/sales/affiliates" element={<SalesAffiliatePage />} />
                <Route path="/sales/copilot" element={<SalesCopilotPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
        </div>
        <BackgroundTaskBar />
      </BrowserRouter>
    </BackgroundTaskProvider>
  );
};
